import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class GlobalFilterService {
  protected filters: any = {
    queryParams: {},
    requestParams: {},
  };
  constructor(protected http: HttpClient) { }
  protected widgetRefreshSubject = new Subject<string>();
  protected widgetId: string | null = null;
  protected filterSubject = new Subject<any>();
  protected currentSelectedFilter = new Subject<any>();
  protected selectedFilters: { [key: string]: any } = {};

  filter$ = this.filterSubject.asObservable();
  currentFilter$ = this.currentSelectedFilter.asObservable();
  widgetRefresh$ = this.widgetRefreshSubject.asObservable();
  activeFilter!: any;
  dropdownOptions!: { [key: string]: string[] };

  private normalizeForCompare(value: any): any {
    if (Array.isArray(value)) {
      return value
        .map((v) => this.normalizeForCompare(v))
        .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
    }

    if (value && typeof value === 'object') {
      return Object.keys(value)
        .sort()
        .reduce((acc: any, k: string) => {
          acc[k] = this.normalizeForCompare(value[k]);
          return acc;
        }, {});
    }

    return value;
  }

  private areValuesEqual(a: any, b: any): boolean {
    return JSON.stringify(this.normalizeForCompare(a)) === JSON.stringify(this.normalizeForCompare(b));
  }

  protected cloneValue(value: any): any {
    if (Array.isArray(value)) return value.map((v) => this.cloneValue(v));
    if (value && typeof value === 'object') return { ...value };
    return value;
  }

  isSelected(key: string, value: any): boolean {
    return this.areValuesEqual(this.selectedFilters[key], value);
  }
  updateFilterSelection(
    keyOrKeys: string | string[],
    values: any[],
    isQueryParam = false,
    widgetId?: string,
    emit: boolean = true,
    filterListener?: string | string[],
    listenerExtraParams?: { [widgetId: string]: { [key: string]: any } },
  ): void {
    const keys = Array.isArray(keyOrKeys)
      ? keyOrKeys
      : (keyOrKeys || '')
        .split(',')
        .map((k) => k.trim())
        .filter((k) => k);

    keys.forEach((key) => {
      const valueToStore = this.cloneValue(values);
      this.selectedFilters[key] = valueToStore;

      if (isQueryParam) {
        this.filters.queryParams[key] = this.cloneValue(values);
      } else {
        this.filters.requestParams[key] = this.cloneValue(values);
      }
    });
    this.widgetId = widgetId || null;
    if (emit) {
      const filterData = {
        ...this.filters,
        widgetId: this.widgetId,
        changedFilterKey: keys.length === 1 ? keys[0] : keys,
        filterListener,
        ...(listenerExtraParams ? { listenerExtraParams } : {}),
      };
      this.filterSubject.next(filterData);
      this.currentSelectedFilter.next({ key: keys });
    }
  }

   requestWidgetRefresh(widgetId: string): void {
    this.widgetRefreshSubject.next(widgetId);
  }

  resetFilterSelection(
    key: string,
    isQueryParam = false,
    filterListener?: string | string[],
  ): void {
    delete this.selectedFilters[key];
    if (isQueryParam) {
      delete this.filters.queryParams[key];
    } else {
      delete this.filters.requestParams[key];
    }
    // Forward filterListener so only targeted charts reload (not ALL charts)
    this.filterSubject.next({
      ...this.filters,
      widgetId: this.widgetId,
      ...(filterListener ? { filterListener } : {}),
    });
  }

  deleteKey(key: string, isQueryParam = false): void {
    delete this.selectedFilters[key];
    if (isQueryParam) {
      delete this.filters.queryParams[key];
    } else {
      delete this.filters.requestParams[key];
    }
  }

  getCurrentFilters(): any {
    return this.filters;
  }

  getNavigationFiltersForWidget(widgetId?: string): any | undefined {
    if (!widgetId) return undefined;

    const filters = this.getCurrentFilters();
    const navigationRequestParams = filters?.navigationRequestParams;
    if (!navigationRequestParams || typeof navigationRequestParams !== 'object') {
      return undefined;
    }

    const listeners = this.normalizeFilterListener(filters?.navigationFilterListener);
    if (!listeners.length || !listeners.includes(widgetId)) {
      return undefined;
    }

    return {
      ...filters,
      requestParams: {
        ...(filters?.requestParams || {}),
        ...navigationRequestParams,
      },
      widgetId: null,
      filterListener: listeners,
      sourceWidgetId: filters?.navigationSourceWidgetId || filters?.sourceWidgetId,
    };
  }

  updateAllFilters(newFilters: any): void {
    this.filters = newFilters;
    this.selectedFilters = {};
    this.filterSubject.next(this.filters);
    this.currentSelectedFilter.next({});
  }

  clearAllFilters() {
    this.filterSubject.next({
      queryParams: {},
      requestParams: {},
      widgetId: null,
      changedFilterKey: null,
    });
    this.selectedFilters = {};
    this.currentSelectedFilter.next('');
  }

  emitCurrentFilters(): void {
    this.filterSubject.next({
      ...this.filters,
      widgetId: null,
    });
  }

  initialTrigger() {
    // skipReload: true tells charts NOT to fire an API call for this emission.
    // Charts already load themselves via ngAfterViewInit — this trigger is only
    // for filter dropdowns to populate, not for charts to reload.
    this.filterSubject.next({
      queryParams: {},
      requestParams: {},
      widgetId: null,
      skipReload: true,
    });
  }

  private normalizeFilterListener(rawValue: unknown): string[] {
    const rawList = Array.isArray(rawValue)
      ? rawValue
      : typeof rawValue === 'string'
        ? rawValue.split(',')
        : [];

    return rawList
      .map((item) => String(item || '').trim())
      .filter((item) => !!item);
  }

  downloadExcel(apiUrl: string, filterPayload: any): Observable<Blob> {
    return this.http.post(apiUrl, filterPayload, {
      responseType: 'blob',
      withCredentials: false, // 👈 STOPS interceptor from reading undefined cookie
    });
  }

  buildPayload(config: any, filters: any[]): any {
    if (config?.skipDefaultPayload) {
      const basePayload =
        config?.customPayload === true
          ? {}
          : config?.customPayload || config?.defaultRequestData || config?.defaultRequesData || {};

      const requestPayload = Array.isArray(filters)
        ? filters.reduce((acc: any, filter: any) => {
            const key = filter?.attributeName;
            const values = filter?.attributeValues ?? filter?.attributeValue;
            if (!key || values === undefined || values === null) {
              return acc;
            }

            if (Array.isArray(values) && values.length === 0) {
              return acc;
            }

            acc[key] = values;
            return acc;
          }, {})
        : {};

      const mergedPayload = {
        ...(basePayload && typeof basePayload === 'object' ? basePayload : {}),
        ...requestPayload,
      };

      const preserveNulls = Object.keys(requestPayload).length === 0;
      return Object.entries(mergedPayload).reduce((acc: any, [key, value]) => {
        const isEmptyArray = Array.isArray(value) && value.length === 0;
        if (value === undefined || isEmptyArray) {
          return acc;
        }
        if (!preserveNulls && value === null) {
          return acc;
        }
        acc[key] = value;
        return acc;
      }, {});
    }

    const defaultRequestData =
      config?.defaultRequestData || config?.defaultRequesData || config?.defaultPayload || {};
    const includeFiltersInRequestData = config?.includeFiltersInRequestData === true;

    const requestData = {
      ...defaultRequestData,
      ...(includeFiltersInRequestData ? { filters: filters } : {}),
    };

    return {
      requestData,
      responseList: [],
      entity: config?.entity || 'default_entity',
      workflowId: config?.workflowId || 'default_workflow',
      endpoints: [
        {
          functionName: config?.functionName || 'DefaultService',
          version: config?.version || '1.0',
        },
      ],
      communicationType: 'rest',
      version: config?.version || '1.0',
    };
  }
}
